// ─── Types ───────────────────────────────────────────────────────────────────

type LogLevel = "debug" | "info" | "log" | "warn" | "error";

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data: unknown[] | null;
  url: string;
  userAgent: string;
  environment: string;
}

// ─── Configuration ───────────────────────────────────────────────────────────

const FLUSH_INTERVAL_MS = 10_000;
const MAX_BUFFER_SIZE = 50;
const LOG_SERVER_URL = "http://localhost:4000/api/frontend-logs";
const ENV = process.env.REACT_APP_ENV || "local";
const IS_LOCAL = ENV === "local";

const LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  log: 2,
  warn: 3,
  error: 4,
};
const MIN_LEVEL: LogLevel = ENV === "prod" ? "warn" : "debug";

// ─── State ───────────────────────────────────────────────────────────────────

let buffer: LogEntry[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

const originalConsole: Record<LogLevel, (...args: unknown[]) => void> = {
  debug: console.debug.bind(console),
  info: console.info.bind(console),
  log: console.log.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function shouldCapture(level: LogLevel): boolean {
  return LEVEL_PRIORITY[level] >= LEVEL_PRIORITY[MIN_LEVEL];
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, (_key, val) => {
      if (val instanceof Error) {
        return { message: val.message, stack: val.stack, name: val.name };
      }
      if (typeof val === "function") return "[Function]";
      if (typeof val === "symbol") return val.toString();
      if (val === undefined) return "[undefined]";
      return val;
    });
  } catch {
    return "[unserializable]";
  }
}

function buildEntry(level: LogLevel, args: unknown[]): LogEntry {
  const [first, ...rest] = args;
  return {
    timestamp: new Date().toISOString(),
    level,
    message: typeof first === "string" ? first : safeStringify(first),
    data: rest.length > 0 ? JSON.parse(safeStringify(rest)) : null,
    url: window.location.href,
    userAgent: navigator.userAgent,
    environment: ENV,
  };
}

// ─── Flush logic ─────────────────────────────────────────────────────────────

function flush(): void {
  if (buffer.length === 0) return;

  const batch = buffer;
  buffer = [];

  fetch(LOG_SERVER_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ logs: batch }),
    keepalive: true,
  }).catch(() => {});
}

function beaconFlush(): void {
  if (buffer.length === 0) return;

  const batch = buffer;
  buffer = [];

  const blob = new Blob([JSON.stringify({ logs: batch })], {
    type: "application/json",
  });
  navigator.sendBeacon(LOG_SERVER_URL, blob);
}

// ─── Patch console ───────────────────────────────────────────────────────────

function patchConsole(): void {
  const levels: LogLevel[] = ["debug", "info", "log", "warn", "error"];

  for (const level of levels) {
    console[level] = (...args: unknown[]) => {
      originalConsole[level](...args);

      if (shouldCapture(level)) {
        buffer.push(buildEntry(level, args));

        if (buffer.length >= MAX_BUFFER_SIZE) {
          flush();
        }
      }
    };
  }
}

// ─── Public API ──────────────────────────────────────────────────────────────

export function initConsoleLogger(): void {
  if (!IS_LOCAL) return;

  patchConsole();

  flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      beaconFlush();
    }
  });

  window.addEventListener("beforeunload", () => {
    beaconFlush();
  });
}

export function teardownConsoleLogger(): void {
  const levels: LogLevel[] = ["debug", "info", "log", "warn", "error"];
  for (const level of levels) {
    console[level] = originalConsole[level];
  }
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  flush();
}

// ─── Explicit Logger ─────────────────────────────────────────────────────────
// Usage: import { logger } from "../utils/consoleLogger";
//        logger.info("Role created", { roleId: 123 });
//        logger.error("Failed to save", error);

function logAtLevel(level: LogLevel, message: string, ...data: unknown[]): void {
  // Always print to DevTools
  originalConsole[level](message, ...data);

  // Only buffer and flush locally
  if (!IS_LOCAL) return;

  buffer.push(buildEntry(level, [message, ...data]));

  if (buffer.length >= MAX_BUFFER_SIZE) {
    flush();
  }
}

export const logger = {
  debug: (message: string, ...data: unknown[]) => logAtLevel("debug", message, ...data),
  info: (message: string, ...data: unknown[]) => logAtLevel("info", message, ...data),
  log: (message: string, ...data: unknown[]) => logAtLevel("log", message, ...data),
  warn: (message: string, ...data: unknown[]) => logAtLevel("warn", message, ...data),
  error: (message: string, ...data: unknown[]) => logAtLevel("error", message, ...data),
};
